/**
 * Base de dados local (IndexedDB, via Dexie).
 *
 * O nucleo nao conhece dominios (regra 1), logo nao pode ter tabelas fixas de
 * "despesa" ou "medicamento". As tabelas de negocio nascem dos
 * `EntityDescriptor` que os plugins declaram no manifesto.
 *
 * O Dexie exige o esquema ANTES de abrir. Dai o `SchemaRegistry`: os plugins
 * registam as entidades no arranque, a lista fecha, e so entao abrimos. Como o
 * conjunto de tabelas muda quando o usuário liga/desliga plugins, guardamos a
 * lista conhecida em `_meta` e derivamos dela um numero de versao monotono —
 * subir a versao e a unica forma de o IndexedDB aceitar novas object stores.
 */
import Dexie, { type Table } from 'dexie';
import type { AuditEntry, ConflictReport, EntityDescriptor, Logger, OutboxItem } from '@flos/shared';

export const DEFAULT_DATABASE_NAME = 'flos';

/** Tabelas do nucleo. O prefixo `_` separa-as das entidades dos plugins. */
export const CORE_TABLES = {
  AUDIT: '_audit',
  OUTBOX: '_outbox',
  SETTINGS: '_settings',
  META: '_meta',
  CONFLICTS: '_conflicts',
} as const;

/** Chave em `_meta` com o esquema conhecido da ultima abertura. */
export const SCHEMA_META_KEY = 'schema.tables';
/** Chave em `_meta` com o ultimo despejo do buffer de diagnostico. */
export const DIAGNOSTICS_META_KEY = 'diagnostics.log';

export interface MetaRow {
  key: string;
  value: unknown;
  updatedAt: string;
}

export interface SettingsRow {
  /** `<namespace>:<nome>`. */
  key: string;
  namespace: string;
  name: string;
  value: unknown;
  updatedAt: string;
}

/**
 * Entrada de auditoria como fica gravada. Estende o contrato partilhado com
 * `revertedFrom`, que o `AuditEntry` nao previu mas sem o qual nao se distingue
 * uma correcao manual de uma reversao (ver relatorio de lacunas).
 */
export interface AuditRow extends AuditEntry {
  revertedFrom?: string;
}

/** Conflito salvo localmente, a espera de decisao humana. */
export interface ConflictRow extends ConflictReport {
  detectedAt: string;
  resolvedAt?: string | null;
}

interface StoredSchema {
  tables: string[];
  version: number;
}

/**
 * Guarda as entidades que os plugins declaram, antes de a base de dados abrir.
 * Depois de aberta, registrar mais nada tem efeito ate o proximo arranque — e o
 * `SchemaRegistry` diz isso alto em vez de falhar em silencio.
 */
export class SchemaRegistry {
  readonly #entities = new Map<string, EntityDescriptor>();
  #sealed = false;
  #logger: Logger | undefined;

  constructor(logger?: Logger) {
    this.#logger = logger;
  }

  setLogger(logger: Logger): void {
    this.#logger = logger;
  }

  register(descriptors: readonly EntityDescriptor[], owner = 'nucleo'): void {
    for (const descriptor of descriptors) {
      const existing = this.#entities.get(descriptor.name);
      if (existing && existing !== descriptor) {
        // Duas entidades com o mesmo nome significam dois donos — proibido pela
        // regra 2 (cada plugin possui o seu namespace).
        throw new Error(
          `A entidade "${descriptor.name}" ja estava registrada. Cada entidade tem um so dono (plugin "${owner}").`,
        );
      }
      if (this.#sealed) {
        this.#logger?.warn(
          'Entidade registrada depois de a base de dados abrir; so estara disponivel no proximo arranque',
          { entity: descriptor.name, plugin: owner },
        );
      }
      this.#entities.set(descriptor.name, descriptor);
    }
  }

  get(name: string): EntityDescriptor | undefined {
    return this.#entities.get(name);
  }

  has(name: string): boolean {
    return this.#entities.has(name);
  }

  all(): EntityDescriptor[] {
    return [...this.#entities.values()];
  }

  names(): string[] {
    return [...this.#entities.keys()].sort();
  }

  /** Entidades cujo conteudo nunca pode ir para registros nem para a nuvem. */
  sensitiveNames(): string[] {
    return this.all()
      .filter((entity) => entity.sensitivity === 'high')
      .map((entity) => entity.name);
  }

  seal(): void {
    this.#sealed = true;
  }

  get sealed(): boolean {
    return this.#sealed;
  }
}

/** Campos base indexados em todas as entidades, alem da chave primaria. */
const BASE_INDEXES = ['familyId', 'updatedAt', 'deletedAt', 'syncState'] as const;

export function buildStores(entities: readonly EntityDescriptor[]): Record<string, string> {
  const stores: Record<string, string> = {
    [CORE_TABLES.AUDIT]:
      'id, familyId, entity, recordId, memberId, at, actor, [entity+recordId], [familyId+at]',
    [CORE_TABLES.OUTBOX]: 'id, entity, recordId, queuedAt, attempts, nextAttemptAt',
    [CORE_TABLES.SETTINGS]: 'key, namespace',
    [CORE_TABLES.META]: 'key',
    [CORE_TABLES.CONFLICTS]: 'outboxItemId, entity, recordId, detectedAt',
  };

  for (const entity of entities) {
    const indexes: string[] = [...BASE_INDEXES];
    for (const extra of entity.indexes) {
      const trimmed = extra.trim();
      if (!trimmed || trimmed === 'id' || indexes.includes(trimmed)) continue;
      indexes.push(trimmed);
    }
    // `[familyId+updatedAt]` serve a listagem por familia ordenada por recencia,
    // que e praticamente todos as telas do aplicativo.
    indexes.push('[familyId+updatedAt]');
    stores[entity.name] = ['id', ...indexes].join(', ');
  }
  return stores;
}

export class FlosDatabase extends Dexie {
  readonly schema: SchemaRegistry;

  constructor(name: string, schema: SchemaRegistry) {
    super(name);
    this.schema = schema;
  }

  get audit(): Table<AuditRow, string> {
    return this.table(CORE_TABLES.AUDIT);
  }

  get outbox(): Table<OutboxItem, string> {
    return this.table(CORE_TABLES.OUTBOX);
  }

  get settingsTable(): Table<SettingsRow, string> {
    return this.table(CORE_TABLES.SETTINGS);
  }

  get meta(): Table<MetaRow, string> {
    return this.table(CORE_TABLES.META);
  }

  get conflicts(): Table<ConflictRow, string> {
    return this.table(CORE_TABLES.CONFLICTS);
  }

  /**
   * Tabela de uma entidade de negocio. Falha cedo e em portugues quando o nome
   * nao existe — quase sempre e um erro de escrita no manifesto.
   */
  records<T = unknown>(entity: string): Table<T, string> {
    if (!this.schema.has(entity)) {
      const conhecidas = this.schema.names().join(', ') || 'nenhuma';
      throw new Error(
        `A entidade "${entity}" nao esta registrada. Entidades conhecidas: ${conhecidas}.`,
      );
    }
    return this.table(entity) as Table<T, string>;
  }

  async getMeta<T>(key: string, fallback: T): Promise<T> {
    const row = await this.meta.get(key);
    return row === undefined ? fallback : (row.value as T);
  }

  async setMeta(key: string, value: unknown): Promise<void> {
    await this.meta.put({ key, value, updatedAt: new Date().toISOString() });
  }
}

export interface OpenDatabaseOptions {
  name?: string;
  schema: SchemaRegistry;
  logger?: Logger;
}

/**
 * Abre a base de dados com o esquema derivado das entidades registradas.
 * O numero de versao e monotono: nunca desce, e so sobe quando o conjunto de
 * tabelas muda. Assim ligar um plugin novo e uma migracao trivial, e desligá-lo
 * nao apaga nada (as tabelas continuam la, apenas deixam de ser usadas).
 */
export async function openDatabase(options: OpenDatabaseOptions): Promise<FlosDatabase> {
  const name = options.name ?? DEFAULT_DATABASE_NAME;
  const { schema, logger } = options;

  const stores = buildStores(schema.all());
  const desiredTables = Object.keys(stores).sort();
  const { version, changed } = await resolveSchemaVersion(name, desiredTables);

  const db = new FlosDatabase(name, schema);
  db.version(version).stores(stores);
  await db.open();
  schema.seal();

  const payload: StoredSchema = { tables: desiredTables, version };
  await db.setMeta(SCHEMA_META_KEY, payload);

  logger?.info('Base de dados local pronta', {
    count: desiredTables.length,
    version,
    changed,
  });
  return db;
}

/**
 * Descobre a versao a usar sem abrir a base de dados definitiva: abre uma
 * instancia "sonda" em modo dinamico (sem esquema), le o que la esta e fecha.
 */
async function resolveSchemaVersion(
  name: string,
  desiredTables: string[],
): Promise<{ version: number; changed: boolean }> {
  const exists = await Dexie.exists(name).catch(() => false);
  if (!exists) return { version: 1, changed: true };

  const probe = new Dexie(name);
  let stored: StoredSchema | null = null;
  let previousVersion = 1;
  let existingTables: string[] = [];

  try {
    await probe.open();
    previousVersion = Math.max(1, Math.floor(probe.verno));
    existingTables = probe.tables.map((table) => table.name).sort();
    if (existingTables.includes(CORE_TABLES.META)) {
      const row = (await probe.table(CORE_TABLES.META).get(SCHEMA_META_KEY)) as
        | MetaRow
        | undefined;
      const value = row?.value as StoredSchema | undefined;
      if (value && Array.isArray(value.tables) && typeof value.version === 'number') {
        stored = value;
      }
    }
  } catch {
    // Base de dados existente mas ilegivel em modo dinamico: seguimos com o que
    // temos. Na pior das hipoteses subimos uma versao a mais, o que e inofensivo.
  } finally {
    probe.close();
  }

  const knownTables = stored?.tables ?? existingTables;
  const baseVersion = Math.max(previousVersion, stored?.version ?? 1);
  const changed = !sameSet(knownTables, desiredTables);
  return { version: changed ? baseVersion + 1 : baseVersion, changed };
}

function sameSet(a: readonly string[], b: readonly string[]): boolean {
  if (a.length !== b.length) return false;
  const set = new Set(a);
  return b.every((item) => set.has(item));
}
